import { pgTable, text, boolean, timestamp, integer, index, uniqueIndex, foreignKey, jsonb } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { notificationChannel, notificationType, broadcastStatus, messageTemplateType, messageTemplateChannel } from './enums';
import { user } from './user';
import { adminUser } from './admin';

export const notification = pgTable('Notification', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer(),
  adminUserId: integer(),
  type: notificationType().notNull(),
  channel: notificationChannel().notNull(),
  title: text().notNull(),
  message: text().notNull(),
  linkUrl: text(),
  isRead: boolean().default(false).notNull(),
  readAt: timestamp({ precision: 3, mode: 'date' }),
  actorAdminId: integer(),
  actorUserId: integer(),
  actorName: text(),
  sentAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('Notification_adminUserId_idx').using('btree', table.adminUserId.asc().nullsLast()),
  index('Notification_isRead_idx').using('btree', table.isRead.asc().nullsLast()),
  index('Notification_sentAt_idx').using('btree', table.sentAt.asc().nullsLast()),
  index('Notification_type_idx').using('btree', table.type.asc().nullsLast()),
  index('Notification_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('Notification_actorAdminId_idx').using('btree', table.actorAdminId.asc().nullsLast()),
  index('Notification_actorUserId_idx').using('btree', table.actorUserId.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'Notification_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'Notification_adminUserId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.actorAdminId],
    foreignColumns: [adminUser.id],
    name: 'Notification_actorAdminId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.actorUserId],
    foreignColumns: [user.id],
    name: 'Notification_actorUserId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

export const notificationTemplate = pgTable('NotificationTemplate', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: text().notNull(),
  eventType: text().notNull(),
  channel: notificationChannel().notNull(),
  subject: text(),
  bodyTemplate: text().notNull(),
  isActive: boolean().default(true).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('NotificationTemplate_eventType_channel_key').using('btree', table.eventType.asc().nullsLast(), table.channel.asc().nullsLast()),
]);

/**
 * MessageTemplate — единая таблица шаблонов сообщений.
 *
 * Заменяет EmailTemplate + InvitationTemplate. Вмещает два паттерна через
 * nullable-колонки и partial unique indexes:
 *  - singleton (`slug IS NULL`): один шаблон на (type, lang) — письма
 *    OTP/ADMIN_INVITE/ADMIN_RESET/NOTIFICATION/ORDER_INVOICE/INVITATION_EMAIL,
 *    авторезолв по типу+языку.
 *  - multi-named (`slug IS NOT NULL`): несколько именованных шаблонов одного
 *    типа со slug/name/isDefault — Telegram-приглашения (INVITATION_TELEGRAM).
 *
 * Множественность (singleton/multi-named) не хранится в БД — выводится из
 * единого конфига типов (@masters-club/types), а partial-индексы физически
 * гарантируют инвариант.
 */
export const messageTemplate = pgTable('MessageTemplate', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  type: messageTemplateType().notNull(),
  channel: messageTemplateChannel().notNull(),
  // singleton: язык письма; multi-named TG: sentinel 'ru' (язык не используется)
  lang: text().notNull(),
  // multi-named only (для singleton — NULL)
  slug: text(),
  name: text(),
  isDefault: boolean().default(false).notNull(),
  // email only (Telegram-сообщения без темы)
  subject: text(),
  // TipTap-документ для визуального редактора, email only
  docJson: jsonb(),
  // Handlebars-шаблон; для TG-приглашений сюда мигрирует bodyTemplate
  htmlTemplate: text().notNull(),
  isActive: boolean().default(true).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  // singleton: уникальность (type, lang) только когда slug IS NULL
  uniqueIndex('MessageTemplate_type_lang_singleton_key')
    .using('btree', table.type.asc().nullsLast(), table.lang.asc().nullsLast())
    .where(sql`${table.slug} IS NULL`),
  // multi-named: уникальность slug только когда slug IS NOT NULL
  uniqueIndex('MessageTemplate_slug_key')
    .using('btree', table.slug.asc().nullsLast())
    .where(sql`${table.slug} IS NOT NULL`),
  index('MessageTemplate_type_idx').using('btree', table.type.asc().nullsLast()),
]);

export const broadcast = pgTable('Broadcast', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: text().notNull(),
  /**
   * Бот, через который отправляется рассылка (TELEGRAM-канал).
   * Также подставляет {{botUsername}} в шаблон. NULL — fallback на первого
   * активного бота (legacy записи и EMAIL-only-рассылки).
   * FK объявлен в relations.ts (inline metadata избегается из-за circular import telegram.ts↔notification.ts).
   */
  botId: integer(),
  messageTemplate: text().notNull(),
  parseMode: text().default('HTML'),
  channels: notificationChannel().array(),
  payload: jsonb(),
  filters: jsonb(),
  status: broadcastStatus().default('DRAFT').notNull(),
  totalRecipients: integer().default(0).notNull(),
  sentCount: integer().default(0).notNull(),
  failedCount: integer().default(0).notNull(),
  skippedCount: integer().default(0).notNull(), // Users skipped due to missing translation
  scheduledAt: timestamp({ precision: 3, mode: 'date' }),
  startedAt: timestamp({ precision: 3, mode: 'date' }),
  completedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('Broadcast_scheduledAt_idx').using('btree', table.scheduledAt.asc().nullsLast()),
  index('Broadcast_status_idx').using('btree', table.status.asc().nullsLast()),
]);

export const broadcastDelivery = pgTable('BroadcastDelivery', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  broadcastId: integer().notNull(),
  userId: integer().notNull(),
  channel: notificationChannel().notNull(),
  status: text().notNull(),
  error: text(),
  sentAt: timestamp({ precision: 3, mode: 'date' }),
  // Денормализация открытий email-рассылки (источник правды — EmailDelivery).
  // Заполняется только для channel=EMAIL при hit'е трекинг-пикселя.
  firstOpenedAt: timestamp({ precision: 3, mode: 'date' }),
  lastOpenedAt: timestamp({ precision: 3, mode: 'date' }),
  openCount: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('BroadcastDelivery_broadcastId_idx').using('btree', table.broadcastId.asc().nullsLast()),
  index('BroadcastDelivery_channel_idx').using('btree', table.channel.asc().nullsLast()),
  index('BroadcastDelivery_userId_idx').using('btree', table.userId.asc().nullsLast()),
  foreignKey({
    columns: [table.broadcastId],
    foreignColumns: [broadcast.id],
    name: 'BroadcastDelivery_broadcastId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'BroadcastDelivery_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);
